<template>
	<div class="title">
		<Chip v-tooltip="step.errorCount && !store.state.isAdminUser ? $t('Squeeze.Queue.ErrorMessageByErrorCount') : $t('Squeeze.Queue.Steps.' + step.name)" class="p-shadow-4" @mouseleave="onMouseLeave">
			<span :class="(step.name === 'Validation' && step.count > 0) || (store.state.scopes.sqzRequeue && step.count > 0) || ($route.name === 'QueueEntryView' && !store.state.scopes.sqzRequeue && step.count > 0) ? 'count-chip' : 'entry-chip'" @click="onClickQueueStep(step.name, '!ERROR')">
				<transition name="flip" mode="out-in">
					<i :key="step.count" :class="icon" style="fontSize: 1rem"></i>
				</transition>
				<div class="p-my-2 p-pl-3" style="display:inline" >
					<transition name="flip" mode="out-in">
						<p :key="step.count" class="p-m-0">{{step?.count == null ? "?" : step.count}}</p>
					</transition>
				</div>
			</span>

			<span :class="(step.name === 'Validation' && step.count > 0) || (store.state.scopes.sqzRequeue && step.count > 0) || ($route.name === 'QueueEntryView' && !store.state.scopes.sqzRequeue && step.count > 0) ? 'count-chip' : 'entry-chip'" @click="onClickQueueStep(step.name, 'ERROR')">
				<transition name="flip" mode="out-in" v-if="step.errorCount">
					<i :key="step.errorCount" class="mdi mdi-progress-alert error-icon"></i>
				</transition>
				<div v-if="step.errorCount" class="p-my-2 p-pl-3 error-count">
					<transition name="flip" mode="out-in">
						<p :key="step.errorCount" class="p-m-0">{{step?.errorCount == null ? "?" : step.errorCount}}</p>
					</transition>
				</div>
			</span>
		</Chip>
	</div>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, ref} from 'vue';
import { useI18n } from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";
import {getSqueezeStepIconMap} from "@/apps/squeeze/App.vue";
import {DocumentClass, QueueStepDto} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import Chip from "primevue/chip";
import router from "@/router";

export default defineComponent({
	name: "QueueTimelineElement",
	components: {
		Chip,
	},
	props: {
		step: {
			type: Object as PropType<QueueStepDto>,
			required: true,
		},
		allDocumentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
	},
	emits: ['onClickQueueStep'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Store point */
		const store = useSqueezeStore();

		/** Set QueueStep icon */
		const icon = ref<string>("mdi mdi-spin mdi-loading");

		/**
		 * Click on queue step
		 * @param {string} stepName
		 * @param {string} status
		 */
		const onClickQueueStep = (stepName: string, status: string) => {
			// check if the stepName is clickable
			if(!store.state.scopes.sqzRequeue && route.name !== 'QueueEntryView' && stepName !== 'Validation') {
				return
			}

			if(!store.state.scopes.sqzRequeue && route.name === 'QueueView' && stepName === 'Validation') {
				return
			}

			// check step errorCount or count
			if (props.step.errorCount >= 1 || props.step.count >= 1) {
				// check route
				if (router.currentRoute.value.fullPath.includes('/app/administration/queue')) {
					router.push({ name: 'QueueEntryView', params: { stepName: stepName, status: status }});
					emit('onClickQueueStep', stepName, status);
				} else if (store.state.scopes.sqzRequeue && stepName !== 'Validation') {
					router.push({ name: 'QueueEntryView', params: { stepName: stepName, status: status }});
				} else {
					if (route.name === "DocumentListValidation" || route.name === "DocumentListValidationWithSearch" && stepName === "Validation") {
						// If you are already in the validation view (document list) when you click on the validation step, then nothing should happen.
						return;
					} else if (props.allDocumentClasses.length === 1) {
						const documentClassId = props.allDocumentClasses[0].id;
						router.push({name: "DocumentListValidation", params: {documentClassId: (documentClassId as any)}});
					} else {
						router.push({ name: 'QueueView'});
					}
				}
			} else {
				ToastManager.showInfo(toast, t('Toasts.Title.Info'), t('Squeeze.Queue.Filter.NoEntries'));
			}
		}

		/** Triggered the mouse leave event on a chip element to remove all visible tooltips */
		const onMouseLeave = () => {
			const tooltips = document.getElementsByClassName("p-tooltip");
			if (tooltips) {
				for (let i = 0; i < tooltips.length; i ++) {
					tooltips[i].remove();
				}
			}
		}

		/** On page mounted */
		onMounted(() =>{
			const stepIcon = getSqueezeStepIconMap().get(props.step.name);
			if (stepIcon) icon.value = stepIcon;
		})

		return {
			t,
			toast,
			route,
			store,
			icon,
			onClickQueueStep,
			onMouseLeave,
		};
	},
});

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">

.p-chip {
	background-color: var(--dex-secondary-dark-color);
	color: var(--dex-text-color);
}

.flip-enter-active {
	transition: all .3s ease;
}
.flip-leave-active {
	transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.flip-enter, .flip-leave-to {
	transform: rotateY(360deg);
}

.error-icon {
	fontSize: 1rem;
	color: var(--queuestate-error-darker);
	margin-left: 1rem;
}

.error-count {
	display: inline;
	color: var(--queuestate-error-darker);
}

.count-chip {
	display: contents;
	cursor: pointer;
}

.entry-chip {
	display: contents;
}

</style>
